/**
 * Result Type - Type-safe error handling without exceptions
 *
 * A Result represents the outcome of an operation that can either succeed or fail.
 * This pattern forces explicit error handling at compile time and makes error flows visible.
 *
 * @example
 * ```typescript
 * function divide(a: number, b: number): Result<number, string> {
 *   if (b === 0) return err("Division by zero");
 *   return ok(a / b);
 * }
 *
 * const result = divide(10, 2);
 * if (result.ok) {
 *   console.log(result.value); // 5
 * } else {
 *   console.error(result.error);
 * }
 * ```
 */

/**
 * Result type - discriminated union for success/failure
 */
export type Result<T, E = Error> = { ok: true; value: T } | { ok: false; error: E };

/**
 * Creates a successful Result
 */
export function ok<T, E = Error>(value: T): Result<T, E> {
  return { ok: true, value };
}

/**
 * Creates a failed Result
 */
export function err<T, E = Error>(error: E): Result<T, E> {
  return { ok: false, error };
}
